import { createDeal, dealsApi } from "@/api/deals-api";
import { useUser } from "@clerk/clerk-react";
import { useDealsListStore } from "@/store/deals-list-store";
import { useMutation } from "@tanstack/react-query";
import {
  Archive,
  CheckCircle,
  Edit3,
  Gem,
  Trash2,
  X,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/api-error";
import { useBulkDeactivateDeals } from "@/hooks/use-bulk-deactivate-deals";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { BulkArchiveDialog } from "./bulk-archive-dialog";
import { BulkDeactivateDialog } from "./bulk-deactivate-dialog";
import { BulkDeleteDialog } from "./bulk-delete-dialog";
import { BulkStatusUpdateModal } from "./bulk-status-update-modal";
import { AddToBoardButton } from "./add-to-board-menu";
import { AddToListMenu } from "./add-to-list-menu";

interface QuickActionsToolbarProps {
  selectedDealIds: string[];
  onClearSelection: () => void;
  onDealDeleted?: () => void;
  onDealArchived?: () => void;
  workspaceId: string;
}

export const QuickActionsToolbar = ({
  selectedDealIds,
  onClearSelection,
  onDealDeleted,
  onDealArchived,
  workspaceId,
}: QuickActionsToolbarProps) => {
  const { user } = useUser();
  const { refreshDeals, deals } = useDealsListStore();
  const { deactivate: bulkDeactivate, isDeactivating: isBulkDeactivating } =
    useBulkDeactivateDeals(workspaceId);
  const premiumSelectedIds = selectedDealIds.filter(
    (id) => deals[id]?.aiTracked
  );
  const [isBulkStatusModalOpen, setIsBulkStatusModalOpen] = useState(false);
  const [isBulkArchiveModalOpen, setIsBulkArchiveModalOpen] = useState(false);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [isBulkDeactivateModalOpen, setIsBulkDeactivateModalOpen] =
    useState(false);

  const bulkStatusUpdateMutation = useMutation({
    mutationFn: async (status: string) => {
      return dealsApi.bulkChangeStatus(
        workspaceId,
        selectedDealIds,
        status,
        user?.id ?? ""
      );
    },
    onSuccess: async () => {
      toast.success(`Successfully updated ${selectedDealIds.length} deals`);
      // Refresh the deals data from the store
      await refreshDeals(workspaceId);
      onClearSelection();
    },
    onError: (error) => {
      console.error("Bulk status update failed:", error);
      toast.error(getErrorMessage(error, "Failed to update deal statuses"));
    },
  });

  const bulkArchiveMutation = useMutation({
    mutationFn: async () => {
      return createDeal.bulkCreateFlaggedCompany(
        workspaceId,
        selectedDealIds,
        user?.id ?? ""
      );
    },
    onSuccess: async () => {
      toast.success(`Successfully archived ${selectedDealIds.length} deals`);
      // Refresh the deals data after archiving
      await refreshDeals(workspaceId);
      onDealArchived?.();
      onClearSelection();
    },
    onError: (error) => {
      console.error("Bulk archive failed:", error);
      toast.error(getErrorMessage(error, "Failed to archive deals"));
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async () => {
      return createDeal.bulkDeleteFlaggedCompany(workspaceId, selectedDealIds);
    },
    onSuccess: async () => {
      toast.success(`Successfully deleted ${selectedDealIds.length} deals`);
      // Refresh the deals data after deletion
      await refreshDeals(workspaceId);
      onDealDeleted?.();
      onClearSelection();
    },
    onError: (error) => {
      console.error("Bulk delete failed:", error);
      toast.error(getErrorMessage(error, "Failed to delete deals"));
    },
  });

  const handleBulkStatusUpdate = async (status: string) => {
    await bulkStatusUpdateMutation.mutateAsync(status);
  };

  const handleBulkArchive = async () => {
    await bulkArchiveMutation.mutateAsync();
  };

  const handleBulkDelete = async () => {
    await bulkDeleteMutation.mutateAsync();
  };

  const handleBulkDeactivate = async () => {
    await bulkDeactivate(premiumSelectedIds, onClearSelection);
  };

  return (
    <>
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-full shadow-lg px-6 py-3 flex items-center gap-4 animate-in slide-in-from-bottom-4 duration-300">
          {/* Selection Info */}
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-500" />
            <Badge variant="secondary" className="text-sm">
              {selectedDealIds.length} selected
            </Badge>
          </div>

          {/* Divider */}
          <div className="w-px h-6 bg-gray-300 dark:bg-gray-600" />

          {/* Quick Actions */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsBulkStatusModalOpen(true)}
              disabled={bulkStatusUpdateMutation.isPending}
              className="flex items-center gap-2"
            >
              <Edit3 className="h-4 w-4" />
              Update Status
            </Button>

            <AddToListMenu
              selectedDealIds={selectedDealIds}
              workspaceId={workspaceId}
              onDone={onClearSelection}
            />

            <AddToBoardButton
              companyIds={selectedDealIds}
              onDone={onClearSelection}
            />

            {premiumSelectedIds.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsBulkDeactivateModalOpen(true)}
                disabled={isBulkDeactivating}
                className="flex items-center gap-2"
              >
                <Gem className="h-4 w-4" />
                Mark as not premium
              </Button>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsBulkArchiveModalOpen(true)}
              disabled={bulkArchiveMutation.isPending}
              className="flex items-center gap-2 text-yellow-600 hover:text-yellow-700"
            >
              <Archive className="h-4 w-4" />
              Archive
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsBulkDeleteModalOpen(true)}
              disabled={bulkDeleteMutation.isPending}
              className="flex items-center gap-2 text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </Button>
          </div>

          {/* Clear Selection */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearSelection}
            className="ml-2 p-2 h-8 w-8"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Bulk Status Update Modal */}
      <BulkStatusUpdateModal
        isOpen={isBulkStatusModalOpen}
        onClose={() => setIsBulkStatusModalOpen(false)}
        selectedDealIds={selectedDealIds}
        onStatusUpdate={handleBulkStatusUpdate}
        isLoading={bulkStatusUpdateMutation.isPending}
      />

      {/* Bulk Archive Dialog */}
      <BulkArchiveDialog
        isOpen={isBulkArchiveModalOpen}
        onClose={() => setIsBulkArchiveModalOpen(false)}
        selectedDealIds={selectedDealIds}
        onArchive={handleBulkArchive}
        isLoading={bulkArchiveMutation.isPending}
      />

      {/* Bulk Delete Dialog */}
      <BulkDeleteDialog
        isOpen={isBulkDeleteModalOpen}
        onClose={() => setIsBulkDeleteModalOpen(false)}
        selectedDealIds={selectedDealIds}
        onDelete={handleBulkDelete}
        isLoading={bulkDeleteMutation.isPending}
      />

      {/* Bulk Deactivate Dialog */}
      <BulkDeactivateDialog
        isOpen={isBulkDeactivateModalOpen}
        onClose={() => setIsBulkDeactivateModalOpen(false)}
        count={premiumSelectedIds.length}
        onConfirm={handleBulkDeactivate}
        isLoading={isBulkDeactivating}
      />
    </>
  );
};
